import { useMemo, useState } from 'react';
import { AlertTriangle, Flame, ChevronRight, PieChart as PieIcon, List } from 'lucide-react';
import { Button, Skeleton, Badge, SegmentedControl } from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { PieChart } from '@vnetwork/ui/charts';
import {
  useTechnicalTicketSummary,
  useTechnicalTickets,
  type TicketTab,
  type TechnicalTicket,
} from '@/api/dashboard-personal';
import { WidgetCard } from './WidgetCard';

/** Donut slice colors keyed by ticket status (token-driven, dark-mode safe). */
const STATUS_COLOR: Record<string, string> = {
  unassigned: 'var(--vn-warning-solid)',
  in_progress: 'var(--vn-info-solid)',
  completed: 'var(--vn-success-solid)',
  closed: 'var(--vn-fg-subtle)',
};

const PRIORITY_TONE: Record<string, 'danger' | 'warning' | 'neutral'> = {
  high: 'danger',
  medium: 'warning',
  low: 'neutral',
};

/** Strip HTML tags from ticket content for a plain-text preview. */
function stripHtml(html: string): string {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/**
 * My Tickets — technical ticket summary with a status donut (chart view) and a
 * per-tab ticket list (list view). Parity with legacy TechnicalTicketsWidget.
 * Real data from /tickets/technical/*.
 */
export function MyTicketsWidget() {
  const [view, setView] = useState<'chart' | 'list'>('chart');
  const [tab, setTab] = useState<TicketTab>('work-in-progress');

  const { data: summary, isLoading: loadingSummary } = useTechnicalTicketSummary();
  const { data: tickets = [], isLoading: loadingList } = useTechnicalTickets(tab);

  const slices = useMemo(
    () =>
      summary
        ? [
            { name: 'Unassigned', value: summary.unassigned_count, color: STATUS_COLOR.unassigned },
            { name: 'In progress', value: summary.in_progress_count, color: STATUS_COLOR.in_progress },
            { name: 'Completed', value: summary.completed_count, color: STATUS_COLOR.completed },
            { name: 'Closed', value: summary.closed_count, color: STATUS_COLOR.closed },
          ]
        : [],
    [summary],
  );
  const total = slices.reduce((s, x) => s + x.value, 0);

  const tabs: { value: TicketTab; label: string; count: number }[] = [
    { value: 'work-in-progress', label: 'In progress', count: summary?.in_progress_count ?? 0 },
    { value: 'completed', label: 'Completed', count: summary?.completed_count ?? 0 },
    { value: 'closed', label: 'Closed', count: summary?.closed_count ?? 0 },
    { value: 'unassigned', label: 'Unassigned', count: summary?.unassigned_count ?? 0 },
  ];

  return (
    <WidgetCard
      title="My Tickets"
      actions={
        <SegmentedControl
          size="sm"
          aria-label="Ticket view"
          value={view}
          onValueChange={(v) => setView(v as 'chart' | 'list')}
          items={[
            { value: 'chart', label: 'Chart', icon: <PieIcon size={13} /> },
            { value: 'list', label: 'List', icon: <List size={13} /> },
          ]}
        />
      }
    >
      {/* Alert chips — always visible, both views (overdue + high priority) */}
      <div style={{ display: 'flex', gap: 'var(--vn-space-2)', marginBottom: 'var(--vn-space-3)' }}>
        <Badge tone="danger" appearance="subtle" size="sm">
          <AlertTriangle size={12} /> {summary?.overdue_count ?? 0} overdue
        </Badge>
        <Badge tone="warning" appearance="subtle" size="sm">
          <Flame size={12} /> {summary?.high_priority_in_progress ?? 0} high priority
        </Badge>
      </div>

      {view === 'chart' ? (
        loadingSummary ? (
          <Skeleton style={{ height: 220 }} />
        ) : total === 0 ? (
          <EmptyState size="sm" title="No tickets" description="Assigned tickets will show here." />
        ) : (
          <>
            <PieChart
              donut
              height={190}
              data={slices.filter((s) => s.value > 0)}
              valueFormatter={(v) => `${v ?? 0} tickets`}
              centerLabel={
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 'var(--vn-font-size-2xl)', fontWeight: 'var(--vn-font-weight-semibold)' }}>
                    {total}
                  </div>
                  <div style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>Total</div>
                </div>
              }
            />
            {/* Legend — status color + count */}
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: 'var(--vn-space-3)',
                marginTop: 'var(--vn-space-3)',
              }}
            >
              {slices.map((s) => (
                <span
                  key={s.name}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 'var(--vn-space-1)',
                    fontSize: 'var(--vn-font-size-xs)',
                    color: 'var(--vn-fg-muted)',
                  }}
                >
                  <span style={{ width: 10, height: 10, borderRadius: 'var(--vn-radius-sm)', background: s.color }} />
                  {s.name} · {s.value}
                </span>
              ))}
            </div>
          </>
        )
      ) : (
        <>
          <SegmentedControl
            size="sm"
            aria-label="Ticket status"
            value={tab}
            onValueChange={(v) => setTab(v as TicketTab)}
            items={tabs.map((t) => ({ value: t.value, label: `${t.label} (${t.count})` }))}
          />
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 'var(--vn-space-2)', marginTop: 'var(--vn-space-3)' }}>
            {loadingList ? (
              Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} style={{ height: 62 }} />)
            ) : tickets.length === 0 ? (
              <EmptyState size="sm" title="No tickets in this tab" />
            ) : (
              tickets.slice(0, 4).map((tk) => <TicketRow key={tk.ticket_id} ticket={tk} />)
            )}
          </div>
        </>
      )}

      <div
        style={{
          marginTop: 'var(--vn-space-3)',
          paddingTop: 'var(--vn-space-3)',
          borderTop: '1px solid var(--vn-border-subtle)',
          textAlign: 'center',
        }}
      >
        <Button variant="ghost" size="sm">
          View all tickets
        </Button>
      </div>
    </WidgetCard>
  );
}

/** A single ticket row in the list view (title/code + priority badge + meta). */
function TicketRow({ ticket }: { ticket: TechnicalTicket }) {
  const overdue =
    ticket.ticket_estimated_completion_date != null &&
    new Date(ticket.ticket_estimated_completion_date).getTime() < Date.now();
  const meta = [ticket.customer_name, ticket.product_group_name, fmtDate(ticket.ticket_requested_at)]
    .filter(Boolean)
    .join(' · ');

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--vn-space-2)',
        padding: 'var(--vn-space-2) var(--vn-space-3)',
        borderRadius: 'var(--vn-radius-md)',
        background: 'var(--vn-bg-muted)',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-1)', marginBottom: 'var(--vn-space-1)' }}>
          <span style={{ fontSize: 'var(--vn-font-size-sm)', fontWeight: 'var(--vn-font-weight-medium)' }}>
            #{ticket.ticket_id}
          </span>
          <Badge tone={PRIORITY_TONE[ticket.ticket_priority] ?? 'neutral'} appearance="subtle" size="sm">
            {ticket.ticket_priority}
          </Badge>
          {overdue ? (
            <Badge tone="danger" appearance="subtle" size="sm">
              <AlertTriangle size={11} /> overdue
            </Badge>
          ) : null}
        </div>
        <div
          style={{
            fontSize: 'var(--vn-font-size-xs)',
            color: 'var(--vn-fg-default)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {stripHtml(ticket.ticket_content) || '—'}
        </div>
        {meta ? (
          <div
            style={{
              fontSize: 'var(--vn-font-size-xs)',
              color: 'var(--vn-fg-muted)',
              marginTop: 'var(--vn-space-1)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {meta}
          </div>
        ) : null}
      </div>
      <ChevronRight size={16} color="var(--vn-fg-subtle)" style={{ flexShrink: 0 }} />
    </div>
  );
}
